import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
  useRef,
} from 'react';
import { Link } from 'react-router';

import { Icon } from '@components/ui/Icon';
import type { IconName } from '@components/ui/icons';
import { cx } from '@utils/cx';

import styles from './GlassButton.module.scss';
import { useGlass } from './useGlass';

type Tone = 'neutral' | 'primary';
type Size = 'md' | 'lg';

interface SharedProps {
  readonly tone?: Tone;
  readonly size?: Size;
  readonly icon?: IconName;
  readonly className?: string;
  readonly children: ReactNode;
}

const useButtonGlass = (
  ref: RefObject<HTMLElement | null>,
  { tone = 'neutral', size = 'md', className }: Omit<SharedProps, 'children' | 'icon'>,
) =>
  useGlass(ref, {
    variant: 'clear',
    interactive: true,
    className: cx(styles.button, styles[tone], styles[size], className),
  });

const Content = ({ icon, children }: Pick<SharedProps, 'icon' | 'children'>) => (
  <>
    <span>{children}</span>
    {icon && <Icon name={icon} className={styles.icon} />}
  </>
);

type GlassButtonProps = SharedProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

/** A glass button. Defaults to `type="button"` so it never submits by accident. */
export const GlassButton = ({
  tone,
  size,
  icon,
  className,
  children,
  type = 'button',
  ...rest
}: GlassButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const glass = useButtonGlass(ref, { tone, size, className });

  return (
    <button
      {...rest}
      type={type}
      ref={ref}
      className={glass.className}
      style={glass.style}
      data-glass-surface=""
    >
      {glass.filter}
      <Content icon={icon}>{children}</Content>
    </button>
  );
};

type GlassLinkProps = SharedProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'> & {
    /** Internal route, or an absolute URL / mailto: for an external link. */
    readonly to: string;
  };

const isExternal = (to: string): boolean => /^(https?:|mailto:|tel:)/.test(to);

/**
 * A glass link. Internal routes go through the router, with a view
 * transition between pages; anything else is a plain anchor.
 */
export const GlassLink = ({
  tone,
  size,
  icon,
  className,
  children,
  to,
  ...rest
}: GlassLinkProps) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const glass = useButtonGlass(ref, { tone, size, className });
  const common = {
    ...rest,
    ref,
    className: glass.className,
    style: glass.style,
    'data-glass-surface': '',
  };

  return isExternal(to) ? (
    <a {...common} href={to}>
      {glass.filter}
      <Content icon={icon}>{children}</Content>
    </a>
  ) : (
    <Link {...common} to={to} viewTransition>
      {glass.filter}
      <Content icon={icon}>{children}</Content>
    </Link>
  );
};
